<template>
  <AnalyticsWidget
    title="NCR Breakdown"
    subTitle="NCR created during the date ranges. Data calculated from completed NCR orders. All values ex GST"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight">

    <LoadingPanel :loading="loading" />
    <AnalyticsBreakdownTable v-if="!loading">
      <template v-slot:header>
        <tr>
          <th></th>
          <th>NCR Count</th>
          <th><Tooltip label="NCRs compared to the number of regular orders completed during the date ranges" helpStyle>NCR Percent</Tooltip></th>
          <th>Remake Value</th>
          <th>Remake Quantity</th>
          <th>Repaired Value</th>
          <th>Repaired Quantity</th>
          <th>Rejected Value</th>
          <th>Rejected Quantity</th>          
        </tr>
      </template>
      <template v-slot:body>
        <tr class="highlight">
          <td>
            Today
            <InlineHelpPopover title="Today" placement="right" inline>
              <p>Today&apos;s NCRs compared to previous working day&apos;s NCRs on {{formatDate(ncrBreakdownData.day.previous.startDate)}}</p>              
            </InlineHelpPopover>
          </td>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.day.current.ncrCount" :previous="ncrBreakdownData.day.previous.ncrCount">{{ncrBreakdownData.day.current.ncrCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.day.current.ncrPercent" :previous="ncrBreakdownData.day.previous.ncrPercent">{{formatPercent(ncrBreakdownData.day.current.ncrPercent)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.day.current.remakeValue" :previous="ncrBreakdownData.day.previous.remakeValue" highlight>{{formatCurrency(ncrBreakdownData.day.current.remakeValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.day.current.remakeCount" :previous="ncrBreakdownData.day.previous.remakeCount">{{ncrBreakdownData.day.current.remakeCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.day.current.repairValue" :previous="ncrBreakdownData.day.previous.repairValue" highlight>{{formatCurrency(ncrBreakdownData.day.current.repairValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.day.current.repairCount" :previous="ncrBreakdownData.day.previous.repairCount">{{ncrBreakdownData.day.current.repairCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.day.current.rejectValue" :previous="ncrBreakdownData.day.previous.rejectValue" highlight>{{formatCurrency(ncrBreakdownData.day.current.rejectValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.day.current.rejectCount" :previous="ncrBreakdownData.day.previous.rejectCount">{{ncrBreakdownData.day.current.rejectCount}}</AnalyticsBreakdownValueCell>
        </tr>
        <tr>
          <td>
            This Week
            <InlineHelpPopover title="This Week" placement="right" inline>
              <p>This week&apos;s NCRs compared to same day of the previous week&apos;s NCRs from {{formatDate(ncrBreakdownData.week.previous.startDate)}} to {{formatDate(ncrBreakdownData.week.previous.endDate)}}</p>
            </InlineHelpPopover>
          </td>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.week.current.ncrCount" :previous="ncrBreakdownData.week.previous.ncrCount">{{ncrBreakdownData.week.current.ncrCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.week.current.ncrPercent" :previous="ncrBreakdownData.week.previous.ncrPercent">{{formatPercent(ncrBreakdownData.week.current.ncrPercent)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.week.current.remakeValue" :previous="ncrBreakdownData.week.previous.remakeValue" highlight>{{formatCurrency(ncrBreakdownData.week.current.remakeValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.week.current.remakeCount" :previous="ncrBreakdownData.week.previous.remakeCount">{{ncrBreakdownData.week.current.remakeCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.week.current.repairValue" :previous="ncrBreakdownData.week.previous.repairValue" highlight>{{formatCurrency(ncrBreakdownData.week.current.repairValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.week.current.repairCount" :previous="ncrBreakdownData.week.previous.repairCount">{{ncrBreakdownData.week.current.repairCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.week.current.rejectValue" :previous="ncrBreakdownData.week.previous.rejectValue" highlight>{{formatCurrency(ncrBreakdownData.week.current.rejectValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.week.current.rejectCount" :previous="ncrBreakdownData.week.previous.rejectCount">{{ncrBreakdownData.week.current.rejectCount}}</AnalyticsBreakdownValueCell>
        </tr>
        <tr>
          <td>
            This Month
            <InlineHelpPopover title="This Week" placement="right" inline>
              <p>This month&apos;s NCRs compared to same day of the previous month&apos;s NCRs from {{formatDate(ncrBreakdownData.month.previous.startDate)}} to {{formatDate(ncrBreakdownData.month.previous.endDate)}}</p>
            </InlineHelpPopover>
          </td>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.month.current.ncrCount" :previous="ncrBreakdownData.month.previous.ncrCount">{{ncrBreakdownData.month.current.ncrCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.month.current.ncrPercent" :previous="ncrBreakdownData.month.previous.ncrPercent">{{formatPercent(ncrBreakdownData.month.current.ncrPercent)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.month.current.remakeValue" :previous="ncrBreakdownData.month.previous.remakeValue" highlight>{{formatCurrency(ncrBreakdownData.month.current.remakeValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.month.current.remakeCount" :previous="ncrBreakdownData.month.previous.remakeCount">{{ncrBreakdownData.month.current.remakeCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.month.current.repairValue" :previous="ncrBreakdownData.month.previous.repairValue" highlight>{{formatCurrency(ncrBreakdownData.month.current.repairValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.month.current.repairCount" :previous="ncrBreakdownData.month.previous.repairCount">{{ncrBreakdownData.month.current.repairCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.month.current.rejectValue" :previous="ncrBreakdownData.month.previous.rejectValue" highlight>{{formatCurrency(ncrBreakdownData.month.current.rejectValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.month.current.rejectCount" :previous="ncrBreakdownData.month.previous.rejectCount">{{ncrBreakdownData.month.current.rejectCount}}</AnalyticsBreakdownValueCell>
        </tr>
        <tr>
          <td>
            This FY
            <InlineHelpPopover title="This Week" placement="right" inline>
              <p>This financial year&apos;s NCRs compared to same day of the previous financial year&apos;s NCRs from {{formatDate(ncrBreakdownData.financialYear.previous.startDate)}} to {{formatDate(ncrBreakdownData.financialYear.previous.endDate)}}</p>
            </InlineHelpPopover>
          </td>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.financialYear.current.ncrCount" :previous="ncrBreakdownData.financialYear.previous.ncrCount">{{ncrBreakdownData.financialYear.current.ncrCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.financialYear.current.ncrPercent" :previous="ncrBreakdownData.financialYear.previous.ncrPercent">{{formatPercent(ncrBreakdownData.financialYear.current.ncrPercent)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.financialYear.current.remakeValue" :previous="ncrBreakdownData.financialYear.previous.remakeValue" highlight>{{formatCurrency(ncrBreakdownData.financialYear.current.remakeValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.financialYear.current.remakeCount" :previous="ncrBreakdownData.financialYear.previous.remakeCount">{{ncrBreakdownData.financialYear.current.remakeCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.financialYear.current.repairValue" :previous="ncrBreakdownData.financialYear.previous.repairValue" highlight>{{formatCurrency(ncrBreakdownData.financialYear.current.repairValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.financialYear.current.repairCount" :previous="ncrBreakdownData.financialYear.previous.repairCount">{{ncrBreakdownData.financialYear.current.repairCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.financialYear.current.rejectValue" :previous="ncrBreakdownData.financialYear.previous.rejectValue" highlight>{{formatCurrency(ncrBreakdownData.financialYear.current.rejectValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="ncrBreakdownData.financialYear.current.rejectCount" :previous="ncrBreakdownData.financialYear.previous.rejectCount">{{ncrBreakdownData.financialYear.current.rejectCount}}</AnalyticsBreakdownValueCell>
        </tr>
      </template>

    </AnalyticsBreakdownTable>
  </AnalyticsWidget>
</template>


<script>
import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsBreakdownTable from '@/modules/intelligence/components/analyticsBreakdownTable'
import AnalyticsBreakdownValueCell from '@/modules/intelligence/components/analyticsBreakdownValueCell'

export default {
  name: 'NCRBreakdownWidget',
  components: {
    AnalyticsWidget,
    AnalyticsBreakdownTable,
    AnalyticsBreakdownValueCell
  },
  props: {
    filter: Object,
    contactId: Number,
    gridWidth: Number,
    gridHeight: Number
  },
  computed: {
    hasContactFilter() {
      return this.contactId != null
    },
  },
  data() {
    return {
      loading: true,
      ncrBreakdownData: {}
    }
  },
  watch: {
    contactId: function() {
      this.requestData()
    },
    filter: function() {
      this.requestData()
    }
  },
  mounted() {
    this.loading = true
    this.requestData()

    this.subscribeEndpoint('/orders', () => {
      this.requestData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/orders')
  },
  methods: {
    requestData() {
      var params = {
        contactId: this.contactId,
        accountLocationId: this.filter ? this.filter.accountLocationIdList : null
      }

      this.$apiService.analyticsService.getNCRValueBreakdown(params)
      .then(response => {
        this.ncrBreakdownData = response.data
      })
      .finally(() => {
        this.loading = false
      })
      .catch(err => {
      })
    }
  }
}
</script>